"use client";

import { c as _c } from "react/compiler-runtime";
import PencilIcon, { PencilIconSize } from "@/components/utils/icons/PencilIcon";
import type { ReactNode } from "react";
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
interface WaveSettingRowProps {
  readonly canEdit: boolean;
  readonly editLabel: string;
  readonly label: string;
  readonly onOpen: () => void;
  readonly renderEditor: (controls: {
    readonly closeEditor: () => void;
  }) => ReactNode;
  readonly valueLabel: ReactNode;
}
const VIEWPORT_PADDING_PX = 16;
const POPOVER_GAP_PX = 8;
const POPOVER_WIDTH_PX = 256;
const shouldUseBottomSheet = () => {
  if (typeof globalThis.matchMedia !== "function") {
    return false;
  }
  return globalThis.matchMedia("(max-width: 767px), (pointer: coarse)").matches;
};
const isInsideElement = (element: HTMLElement | null, target: EventTarget | null) => {
  if (!element || typeof globalThis.Node === "undefined") {
    return false;
  }
  return target instanceof globalThis.Node && element.contains(target);
};
export default function WaveSettingRow(t0) {
  const $ = _c(61);
  const {
    canEdit,
    editLabel,
    label,
    onOpen,
    renderEditor,
    valueLabel
  } = t0;
  const editorId = useId();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [useBottomSheet, setUseBottomSheet] = useState(_temp);
  const [popoverPosition, setPopoverPosition] = useState(null);
  const editButtonRef = useRef(null);
  const editorRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setIsEditorOpen(false);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const closeEditor = t1;
  let t2;
  let t3;
  if ($[1] !== isEditorOpen) {
    t2 = () => {
      if (!isEditorOpen) {
        return;
      }
      const handlePointerDown = event => {
        if (isInsideElement(editorRef.current, event.target) || isInsideElement(editButtonRef.current, event.target)) {
          return;
        }
        closeEditor();
      };
      const handleKeyDown = event_0 => {
        if (event_0.key === "Escape") {
          closeEditor();
        }
      };
      globalThis.document.addEventListener("mousedown", handlePointerDown);
      globalThis.document.addEventListener("touchstart", handlePointerDown);
      globalThis.document.addEventListener("keydown", handleKeyDown);
      return () => {
        globalThis.document.removeEventListener("mousedown", handlePointerDown);
        globalThis.document.removeEventListener("touchstart", handlePointerDown);
        globalThis.document.removeEventListener("keydown", handleKeyDown);
      };
    };
    t3 = [closeEditor, isEditorOpen];
    $[1] = isEditorOpen;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== onOpen) {
    t4 = () => {
      onOpen();
      setPopoverPosition(null);
      setIsEditorOpen(true);
    };
    $[4] = onOpen;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const openEditor = t4;
  let t5;
  if ($[6] !== isEditorOpen || $[7] !== openEditor) {
    t5 = () => {
      if (isEditorOpen) {
        closeEditor();
        return;
      }
      openEditor();
    };
    $[6] = isEditorOpen;
    $[7] = openEditor;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const toggleEditor = t5;
  let t6;
  if ($[9] !== isEditorOpen || $[10] !== useBottomSheet) {
    t6 = () => {
      if (!isEditorOpen || useBottomSheet) {
        return;
      }
      const button = editButtonRef.current;
      const editor = editorRef.current;
      if (!button || !editor) {
        return;
      }
      const buttonRect = button.getBoundingClientRect();
      const editorWidth = editor.offsetWidth || POPOVER_WIDTH_PX;
      const editorHeight = editor.offsetHeight;
      const viewportWidth = globalThis.innerWidth || globalThis.document.documentElement.clientWidth || POPOVER_WIDTH_PX + VIEWPORT_PADDING_PX * 2;
      const viewportHeight = globalThis.innerHeight || globalThis.document.documentElement.clientHeight || editorHeight + VIEWPORT_PADDING_PX * 2;
      const maxLeft = Math.max(VIEWPORT_PADDING_PX, viewportWidth - VIEWPORT_PADDING_PX - editorWidth);
      const left = Math.min(Math.max(buttonRect.right - editorWidth, VIEWPORT_PADDING_PX), maxLeft);
      const availableAbove = buttonRect.top - VIEWPORT_PADDING_PX;
      const availableBelow = viewportHeight - buttonRect.bottom - VIEWPORT_PADDING_PX;
      const shouldOpenAbove = editorHeight > 0 && availableBelow < editorHeight + POPOVER_GAP_PX && availableAbove > availableBelow;
      const unclampedTop = shouldOpenAbove ? buttonRect.top - editorHeight - POPOVER_GAP_PX : buttonRect.bottom + POPOVER_GAP_PX;
      const maxTop = Math.max(VIEWPORT_PADDING_PX, viewportHeight - VIEWPORT_PADDING_PX - editorHeight);
      const top = Math.min(Math.max(unclampedTop, VIEWPORT_PADDING_PX), maxTop);
      setPopoverPosition({
        left,
        top
      });
    };
    $[9] = isEditorOpen;
    $[10] = useBottomSheet;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const updatePopoverPosition = t6;
  let t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const updateSurfaceMode = () => {
        setUseBottomSheet(shouldUseBottomSheet());
      };
      globalThis.addEventListener("resize", updateSurfaceMode);
      return () => {
        globalThis.removeEventListener("resize", updateSurfaceMode);
      };
    };
    t8 = [];
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[14] !== updatePopoverPosition) {
    t9 = () => {
      updatePopoverPosition();
    };
    t10 = [updatePopoverPosition];
    $[14] = updatePopoverPosition;
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  useLayoutEffect(t9, t10);
  let t11;
  let t12;
  if ($[17] !== isEditorOpen || $[18] !== updatePopoverPosition || $[19] !== useBottomSheet) {
    t11 = () => {
      if (!isEditorOpen || useBottomSheet) {
        return;
      }
      globalThis.addEventListener("resize", updatePopoverPosition);
      globalThis.addEventListener("scroll", updatePopoverPosition, true);
      return () => {
        globalThis.removeEventListener("resize", updatePopoverPosition);
        globalThis.removeEventListener("scroll", updatePopoverPosition, true);
      };
    };
    t12 = [isEditorOpen, updatePopoverPosition, useBottomSheet];
    $[17] = isEditorOpen;
    $[18] = updatePopoverPosition;
    $[19] = useBottomSheet;
    $[20] = t11;
    $[21] = t12;
  } else {
    t11 = $[20];
    t12 = $[21];
  }
  useEffect(t11, t12);
  let t13;
  if ($[22] !== isEditorOpen || $[23] !== renderEditor) {
    t13 = isEditorOpen ? renderEditor({
      closeEditor
    }) : null;
    $[22] = isEditorOpen;
    $[23] = renderEditor;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const editorForm = t13;
  let t14;
  if ($[25] !== editorForm || $[26] !== editorId) {
    t14 = <div id={editorId} ref={editorRef} className="tw-w-full tw-rounded-t-xl tw-border tw-border-b-0 tw-border-solid tw-border-iron-700 tw-bg-iron-950 tw-p-4 tw-shadow-[0_-18px_50px_rgba(0,0,0,0.45)]">{editorForm}</div>;
    $[25] = editorForm;
    $[26] = editorId;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== editLabel || $[29] !== t14) {
    t15 = <div className="tailwind-scope tw-fixed tw-inset-0 tw-z-[1200] tw-flex tw-items-end tw-bg-black/60 tw-px-3 tw-pb-3 tw-pt-10"><dialog open={true} aria-modal="true" aria-label={editLabel} className="tw-relative tw-m-0 tw-w-full tw-max-w-none tw-border-0 tw-bg-transparent tw-p-0 tw-text-inherit">{t14}</dialog></div>;
    $[28] = editLabel;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const bottomSheetSurface = t15;
  const t16 = popoverPosition?.left ?? 0;
  const t17 = popoverPosition?.top ?? 0;
  const t18 = popoverPosition ? "visible" : "hidden";
  let t19;
  if ($[31] !== t16 || $[32] !== t17 || $[33] !== t18) {
    t19 = {
      left: t16,
      top: t17,
      visibility: t18,
      width: POPOVER_WIDTH_PX
    };
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== editorForm || $[36] !== editorId) {
    t20 = <div id={editorId} ref={editorRef} className="tw-w-64 tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-950 tw-p-3 tw-shadow-xl">{editorForm}</div>;
    $[35] = editorForm;
    $[36] = editorId;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t19 || $[39] !== t20) {
    t21 = <div className="tailwind-scope tw-fixed tw-z-[1200]" style={t19}>{t20}</div>;
    $[38] = t19;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  const popoverSurface = t21;
  const selectedEditorSurface = useBottomSheet ? bottomSheetSurface : popoverSurface;
  let t22;
  if ($[41] !== isEditorOpen || $[42] !== selectedEditorSurface) {
    t22 = isEditorOpen ? createPortal(selectedEditorSurface, globalThis.document.body) : null;
    $[41] = isEditorOpen;
    $[42] = selectedEditorSurface;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  const editorSurface = t22;
  let t23;
  if ($[44] !== label) {
    t23 = <span className="tw-font-medium tw-text-iron-400">{label}</span>;
    $[44] = label;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== valueLabel) {
    t24 = <span className="tw-font-medium tw-text-iron-200">{valueLabel}</span>;
    $[46] = valueLabel;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== canEdit || $[49] !== editLabel || $[50] !== editorId || $[51] !== isEditorOpen || $[52] !== toggleEditor) {
    t25 = canEdit && <button ref={editButtonRef} type="button" aria-label={editLabel} aria-controls={editorId} aria-expanded={isEditorOpen} aria-haspopup="dialog" title={editLabel} onClick={toggleEditor} className="tw-border-none tw-bg-transparent tw-p-0 tw-text-iron-300 tw-transition-all tw-duration-300 tw-ease-out desktop-hover:hover:tw-text-iron-400"><PencilIcon size={PencilIconSize.SMALL} /></button>;
    $[48] = canEdit;
    $[49] = editLabel;
    $[50] = editorId;
    $[51] = isEditorOpen;
    $[52] = toggleEditor;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== t24 || $[55] !== t25) {
    t26 = <div className="tw-flex tw-items-center tw-gap-x-2">{t24}{t25}</div>;
    $[54] = t24;
    $[55] = t25;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== editorSurface || $[58] !== t23 || $[59] !== t26) {
    t27 = <div className="tw-group tw-flex tw-min-h-6 tw-w-full tw-items-center tw-justify-between tw-gap-1.5 tw-text-sm">{t23}{t26}{editorSurface}</div>;
    $[57] = editorSurface;
    $[58] = t23;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  return t27;
}
function _temp() {
  return shouldUseBottomSheet();
}